import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, Spinner } from 'react-bootstrap';
import api from '../../services/api';

export interface CloneOptions {
  nome: string;
  checklist: boolean;
  manter_respostas: boolean;
  alterar_escopo: boolean;
  escopo: string | null;
  alterar_tipos: boolean;
  tipo_resposta_alvo: 'SIM_NAO' | 'PERCENTUAL' | null;
  permite_na: boolean;
}

interface CloneQuestionnaireModalProps {
  show: boolean;
  onHide: () => void;
  onConfirm: (opcoes: CloneOptions) => void;
  originalName: string;
  saving: boolean;
  empresaId: number | null;
}

const CloneQuestionnaireModal: React.FC<CloneQuestionnaireModalProps> = ({
  show, onHide, onConfirm, originalName, saving, empresaId,
}) => {
  const [nome, setNome] = useState(`${originalName} - Cópia`);
  const [checklistOn, setChecklistOn] = useState(false);
  const [manterRespostas, setManterRespostas] = useState<'' | 'sim' | 'nao'>('');
  const [alterarEscopo, setAlterarEscopo] = useState<'' | 'sim' | 'nao'>('');
  const [escopo, setEscopo] = useState('');
  const [escopoOptions, setEscopoOptions] = useState<string[]>([]);
  const [tipoAlvo, setTipoAlvo] = useState<'' | 'SIM_NAO' | 'PERCENTUAL'>('');
  const [naOn, setNaOn] = useState(false);

  useEffect(() => {
    if (show) {
      setNome(`${originalName} - Cópia`);
      setChecklistOn(false);
      setManterRespostas('');
      setAlterarEscopo('');
      setEscopo('');
      setTipoAlvo('');
      setNaOn(false);
    }
  }, [show, originalName]);

  // Carrega os escopos cadastrados quando o usuário opta por alterar o escopo.
  useEffect(() => {
    if (alterarEscopo === 'sim' && escopoOptions.length === 0) {
      api.getCadastrosAuxiliares('escopo_questionario', empresaId ?? undefined).then(res => {
        if (res.success) {
          setEscopoOptions(((res.data as any[]) || []).filter(r => r.ativo !== false).map(r => r.nome));
        }
      });
    }
  }, [alterarEscopo, empresaId, escopoOptions.length]);

  const handleConfirm = () => {
    onConfirm({
      nome,
      checklist: checklistOn,
      manter_respostas: manterRespostas === 'sim',
      alterar_escopo: alterarEscopo === 'sim',
      escopo: alterarEscopo === 'sim' ? (escopo || null) : null,
      alterar_tipos: tipoAlvo !== '',
      tipo_resposta_alvo: tipoAlvo === '' ? null : tipoAlvo,
      permite_na: naOn,
    });
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header closeButton><Modal.Title>Clonar Questionário</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form.Group className="mb-3">
          <Form.Label>Nome do clone <span className="text-danger">*</span></Form.Label>
          <Form.Control value={nome} onChange={e => setNome(e.target.value)} autoFocus />
        </Form.Group>

        <h6 className="fw-semibold text-primary mt-4 mb-2">Personalizar Questionário</h6>

        <div className="d-flex justify-content-between align-items-center py-1">
          <span>Checklist</span>
          <Form.Check type="switch" checked={checklistOn} onChange={e => setChecklistOn(e.target.checked)} />
        </div>

        <div className="d-flex justify-content-between align-items-center py-1">
          <span>Manter as perguntas já respondidas?</span>
          <div className="d-flex gap-3">
            <Form.Check type="radio" name="clone-manter-resp" label="Sim" checked={manterRespostas === 'sim'} onChange={() => setManterRespostas('sim')} />
            <Form.Check type="radio" name="clone-manter-resp" label="Não" checked={manterRespostas === 'nao'} onChange={() => setManterRespostas('nao')} />
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center py-1">
          <span>Alterar escopo do Questionário?</span>
          <div className="d-flex gap-3">
            <Form.Check type="radio" name="clone-alterar-escopo" label="Sim" checked={alterarEscopo === 'sim'} onChange={() => setAlterarEscopo('sim')} />
            <Form.Check type="radio" name="clone-alterar-escopo" label="Não" checked={alterarEscopo === 'nao'} onChange={() => setAlterarEscopo('nao')} />
          </div>
        </div>
        {alterarEscopo === 'sim' && (
          <Form.Select size="sm" className="mb-1" value={escopo} onChange={e => setEscopo(e.target.value)}>
            <option value="">Selecione o escopo...</option>
            {escopoOptions.map(o => <option key={o} value={o}>{o}</option>)}
          </Form.Select>
        )}

        <div className="d-flex justify-content-between align-items-center py-1">
          <span>Alterar tipos de resposta (Todos)?</span>
          <div className="btn-group btn-group-sm" role="group">
            <Button variant={tipoAlvo === 'SIM_NAO' ? 'primary' : 'outline-secondary'} onClick={() => setTipoAlvo(t => t === 'SIM_NAO' ? '' : 'SIM_NAO')}>Sim/Não</Button>
            <Button variant={tipoAlvo === 'PERCENTUAL' ? 'primary' : 'outline-secondary'} onClick={() => setTipoAlvo(t => t === 'PERCENTUAL' ? '' : 'PERCENTUAL')}>Percentual</Button>
          </div>
        </div>

        <div className="d-flex justify-content-between align-items-center py-1">
          <span>N/A (Não se aplica)</span>
          <Form.Check type="switch" checked={naOn} onChange={e => setNaOn(e.target.checked)} />
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>Cancelar</Button>
        <Button variant="primary" onClick={handleConfirm} disabled={!nome.trim() || saving || (alterarEscopo === 'sim' && !escopo)}>
          {saving ? <Spinner as="span" size="sm" animation="border" /> : 'Clonar'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default CloneQuestionnaireModal;
